import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../api";
import AccountList from "../Components/Accounts/AccountList";
import AgedInvoicesList from "../Components/Accounts/InvoiceList";
import TransactionList from "../Components/Accounts/TransactionList";
import ClaimList from "../Components/Accounts/ClaimList";
import ERAList from "../Components/Accounts/ERAList";
import AllocationReport from "../Components/Accounts/AllocationReport";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "../Components/Accounts/acc-rail.css";

function Billing() {
  const [practice, setPractice] = useState([]);
  const [userRole, setUserRole] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get active section from URL, default to "account"
  const activeSection = searchParams.get("section") || "account";

  // sections config
  const sidebarItems = useMemo(
    () => [
      { key: "account", label: "Account List", icon: "fa-light fa-list" },
      {
        key: "invoices",
        label: "Open Invoices",
        gated: true,
        icon: "fa-sharp fa-light fa-file-invoice",
      },
      {
        key: "transactions",
        label: "Sales Report",
        gated: true,
        icon: "fa-light fa-cart-arrow-down",
      },
      {
        key: "allocations",
        label: "Allocation Report",
        gated: true,
        icon: "fa-sharp fa-light fa-ballot-check",
      },
      {
        key: "claims",
        label: "Claims",
        gated: true,
        icon: "fa-sharp fa-light fa-file-circle-exclamation",
      },
      {
        key: "eras",
        label: "ERA",
        gated: true,
        icon: "fa-sharp fa-light fa-atom",
      },
      // { key: "payments", label: "Ozow Payments", gated: true, icon: "fa-light fa-money-bill-wave" },
    ],
    [],
  );

  // Function to change section via URL
  const setActiveSection = (section) => {
    navigate(`/billing?section=${section}`, { replace: true });
  };

  // Handle legacy URL params (aging, paymentStatus, claims, eras)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("aging") && !params.get("section")) {
      navigate("/billing?section=invoices", { replace: true });
    }
    if (params.get("paymentStatus") && !params.get("section")) {
      navigate("/billing?section=payments", { replace: true });
    }
    if (params.get("claims") && !params.get("section")) {
      navigate("/billing?section=claims", { replace: true });
    }
    if (params.get("eras") && !params.get("section")) {
      navigate("/billing?section=eras", { replace: true });
    }
  }, [location.search, navigate]);

  // load user + roles
  useEffect(() => {
    async function getData() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;

        const user = jwtDecode(token);

        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
            ? [user.role]
            : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];
        const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];
        const allowedRoles = ["Finance", "SuperAdmin"];
        const hasAllowedRole = roles.some((r) => allowedRoles.includes(r));
        setUserRole(hasAllowedRole);

        const fetchedPractices = await getUserPracticeById(user._id);
        setPractice(Array.isArray(fetchedPractices) ? fetchedPractices : []);
      } catch (err) {
        console.error("⛔ Error fetching practices/roles:", err);
        setPractice([]);
        setUserRole(false);
      }
    }
    getData();
  }, []);

  // render header

  // render content
  function renderContent() {
    switch (activeSection) {
      case "account":
        return <AccountList />;
      case "invoices":
        return <AgedInvoicesList />;
      case "transactions":
        return <TransactionList />;
      case "allocations":
        return <AllocationReport />;
      case "claims":
        return <ClaimList />;
      case "eras":
        return <ERAList />;
      //   case "payments":
      //     return <OzowList />;
      default:
        return <AccountList />;
    }
  }

  return (
    <div style={{ marginTop: "55px" }}>
      {/*  {renderHeader()} */}

      <Container fluid className="px-0">
        <Row className="g-0">
          {/* MOBILE TAB STRIP (<768px) — horizontal-scroll icon row */}
          <Col xs={12} className="d-flex d-md-none pt-1 g-0">
            <Container className="bg-white g-0 shadow-sm rounded">
              <div className="acc-rail-mobile">
                {sidebarItems.map((item) => {
                  if (item.gated && !userRole) return null;
                  const isActive = activeSection === item.key;
                  return (
                    <Button
                      key={item.key}
                      aria-label={item.label}
                      className={isActive ? "acc-rail-item active" : "acc-rail-item"}
                      onClick={() => setActiveSection(item.key)}
                    >
                      <i className={item.icon} />
                    </Button>
                  );
                })}
              </div>
            </Container>
          </Col>

          {/* LEFT PANEL — DESKTOP ICON RAIL (>=768px) */}
          <Col xs="auto" className="d-none d-md-block pt-1 g-0" style={{ width: 64 }}>
            <Container className="bg-white vh-100 g-0 p-1 shadow-sm rounded" style={{ width: 64 }}>
              <div className="acc-rail">
                {sidebarItems.map((item) => {
                  if (item.gated && !userRole) return null;
                  const isActive = activeSection === item.key;
                  return (
                    <Button
                      key={item.key}
                      aria-label={item.label}
                      className={isActive ? "acc-rail-item active" : "acc-rail-item"}
                      onClick={() => setActiveSection(item.key)}
                    >
                      <i className={item.icon} />
                    </Button>
                  );
                })}
              </div>
            </Container>
          </Col>

          {/* RIGHT CONTENT */}
          <Col xs={12} md className="g-0">
            <Container fluid className="pt-3 bg-transparent pb-3 mb-3 rounded">
              <h4 className="text-primary fw-normal mb-3 ms-1">
                {sidebarItems.find((i) => i.key === activeSection)?.label}
              </h4>
              {renderContent()}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Billing;
